import uniqid from "uniqid";

import { Article } from "astrid-firestore/src/api/articles/types/Article";
import { Artifact } from "astrid-firestore/src/api/artifacts/types/Artifact";
import { Imprint } from "astrid-firestore/src/api/imprints/types/Imprint";
import { OrganizationRef } from "astrid-firestore/src/api/organizations/types/OrganizationRef";
import { Serie, SerieRef } from "astrid-firestore/src/api/series/types/Serie";
import { getCollectionData } from "astrid-firestore/src/helpers";

import articleStatuses from "../../src/api/articles/constants/articleStatuses";
import articleTypes from "../../src/api/articles/constants/articleTypes";
import getPublicationStatusByPriority from "../../src/api/articles/getPublicationStatusByPriority";
import validateArticleISBN from "../../src/api/articles/validateArticleISBN";
import { ArtifactFile } from "../../src/api/artifacts/types/ArtifactFile";
import { ImprintRef } from "../../src/api/imprints/types/ImprintRef";
import productionStatuses from "../../src/api/productions/constants/productionStatuses";
import { ProductionRef } from "../../src/api/productions/types/Production";
import { Work } from "../../src/api/works/types/Work";
import { WorkRef } from "../../src/api/works/types/WorkRef";

import createArticleStatus from "./createArticleStatus";
import createAudiobook from "./createAudiobook";
import createAudiobooksFromMulti from "./createAudiobooksFromMulti";
import createCommonArticleData from "./createCommonArticleData";
import createDocumentIdAndRef from "./createDocumentIdAndRef";
import createEbook from "./createEbook";
import createImprint from "./createImprint";
import createSeries from "./createSeries";
import createWork from "./createWork";
import getOrganization from "./getOrganization";

function hasAudioBook(production) {
	return typeof production.isbn === "string";
}

export function hasMulti(production) {
	return production.deliveryParts?.length;
}

function hasEbook(production) {
	return production.deliveryEbook?.isbn;
}

function hasSeries(production) {
	return production.series || production.deliveryPartsTitle;
}

function hasSeason(production) {
	if (!hasMulti(production)) {
		return false;
	}
	return production.part;
}

function hasImprint({ production, overrideImprint }) {
	return production?.biblio?.imprintName || overrideImprint;
}

async function getSeries({ api, env }, { production, name }) {
	const series = await getCollectionData(
		api[env].db.collection("series").where("name", "==", name).where("publisher.id", "==", production.publisher),
	);
	return series.length ? series[0] : null;
}

async function getImprint({ api, env }, { production, publisher, overrideImprint }) {
	const imprint = await getCollectionData(
		api[env].db
			.collection("imprints")
			.where("name", "==", production?.biblio?.imprintName || overrideImprint)
			.where("publisher.id", "==", publisher.id),
	);
	return imprint.length ? imprint[0] : null;
}

export async function migrateProductionToFlattening(
	{ api, env },
	{
		production,
		doMigration = false,
		skipExistingArticles = false,
		switchPublisher = false,
		switchPublisherName = false,
		overrideImprint = false,
		preResolved = null,
	},
) {
	let work = null;
	let articles = [];
	let artifacts = [];
	let ebookArticle = null;
	let articlesMap = {};
	let newSeries = null;
	let seriesRef = null;
	let newImprint = null;
	let imprintRef = null;
	let season = null;

	const db = api.convertFromLiveToStage ? api.stage.db : api[env].db;

	const publisher = await getOrganization(
		{ api, env },
		{
			organizationId: switchPublisher
				? switchPublisher
				: api.convertFromLiveToStage
				? api.convertFromLiveToStage.publisher
				: api.convertToAnotherPublisher
				? api.convertToAnotherPublisher
				: production.publisher,
		},
	);
	const producer = await getOrganization(
		{ api, env },
		{
			organizationId: api.convertFromLiveToStage ? api.convertFromLiveToStage.producer : production.producer,
		},
	);

	if (hasSeries(production)) {
		const seriesName = (production.series || production.deliveryPartsTitle).trim();
		const seriesKey = `${publisher.id}|${seriesName}`;
		const preResolvedSeries = preResolved?.series?.get(seriesKey);
		const existingSeries =
			preResolvedSeries ||
			(await getSeries(
				{
					api,
					env,
				},
				{ production, name: seriesName },
			));

		if (hasSeason(production)) {
			const existingSeason = existingSeries?.seasons.find(
				(s) => s.name === production.part || s.name === production.deliveryPartsTitle,
			);
			season = existingSeason || {
				id: uniqid(),
				name: production.deliveryPartsTitle || production.part.toString(),
			};
		}

		const seriesDoc =
			existingSeries ||
			createSeries(
				{
					api,
					env,
				},
				{
					name: production.series || production.deliveryPartsTitle,
					season,
					publisher,
				},
			);

		if (!existingSeries) {
			newSeries = seriesDoc;
		}
		seriesRef = seriesDoc;
	}

	const commonArticleData = await createCommonArticleData(
		{ api, env },
		{
			production,
			season,
			producer,
			publisher,
		},
	);

	work = await createWork({ api, env }, { production, commonArticleData });

	if (hasEbook(production)) {
		ebookArticle = await createEbook({ api, env }, { production, publisher, commonArticleData });
		artifacts.push(ebookArticle.artifact);
		articles.push(ebookArticle);
		if (ebookArticle.artifact && ebookArticle.artifact?.article) {
			articlesMap[ebookArticle.id] = { artifactIds: [ebookArticle.artifact.id] };
		}
	}

	if (hasAudioBook(production)) {
		const audioBookArticle = await createAudiobook(
			{ api, env },
			{
				production,
				publisher,
				commonArticleData,
				ebookArticle,
			},
		);

		artifacts.push(audioBookArticle.artifact);
		articles.push(audioBookArticle);
		if (audioBookArticle.artifact && audioBookArticle.artifact?.article) {
			articlesMap[audioBookArticle.id] = { artifactIds: [audioBookArticle.artifact.id] };
		}
	}
	if (hasMulti(production)) {
		const audioBookMultiParts = await createAudiobooksFromMulti(
			{ api, env },
			{
				production,
				publisher,
				commonArticleData,
			},
		);

		artifacts.push(...audioBookMultiParts.map((part) => part.artifact));
		articles.push(...audioBookMultiParts);
		audioBookMultiParts.forEach((article) => {
			if (article.artifact && article.artifact?.article) {
				articlesMap[article.id] = { artifactIds: [article.artifact.id] };
			}
		});
	}

	if (hasImprint({ production, overrideImprint })) {
		const imprintName = production?.biblio?.imprintName || overrideImprint;
		const imprintKey = `${publisher.id}|${imprintName}`;
		const preResolvedImprint = preResolved?.imprints?.get(imprintKey);
		const existingImprint =
			preResolvedImprint || (await getImprint({ api, env }, { production, publisher, overrideImprint }));

		const imprintDoc = existingImprint || createImprint({ api, env }, { production, publisher, overrideImprint });

		if (!existingImprint) {
			newImprint = imprintDoc;
		}
		imprintRef = imprintDoc;
	}

	const validArticles = articles.filter((article) => {
		if (!article.isbn) {
			console.log(
				`Skipping article with missing ISBN (prod: ${production.id}, article name: ${article.name}, article type: ${article.type})`,
			);
			return false;
		}

		return true;
	});

	const parsedArticles = validArticles.map((article) => {
		const articleStatus = createArticleStatus({ api, env }, { article });
		return {
			collection: "articles",
			data: Article.parse({
				...article,
				producer: OrganizationRef.parse(producer),
				publisher: OrganizationRef.parse(publisher),
				status: articleStatus,
				channelIds: articleStatus === articleStatuses.DRAFT ? [] : article.channelIds,
				channels: articleStatus === articleStatuses.DRAFT ? {} : article.channels,
				delivery: articleStatus === articleStatuses.DRAFT ? {} : article.delivery,
				publicationStatus: getPublicationStatusByPriority(Object.values(article.channels || {})),
				work: work ? WorkRef.parse(work) : null,
				serie: seriesRef ? SerieRef.parse(seriesRef) : null,
				season: season ? season : null,
				imprint: imprintRef ? ImprintRef.parse(imprintRef) : null,
				created: api.admin.firestore.Timestamp.fromDate(new Date()),
				createdBy: { id: "migrationScript", firstName: "migrationScript", lastName: "", email: "" },
			}),
		};
	});

	let skippedArticles = [];

	if (skipExistingArticles) {
		await Promise.all(
			parsedArticles.map(async ({ data }) => {
				try {
					return await validateArticleISBN(db, data);
				} catch (error) {
					if (error.message === "An article with the same ISBN already exists") {
						console.log(`Skipping article ${data?.isbn} due to duplicate ISBN`);
						skippedArticles.push(data?.isbn);
						delete articlesMap[data.id];
					} else {
						throw error;
					}
				}
			}),
		);
	} else {
		await Promise.all(
			parsedArticles.map(({ data }) => {
				return validateArticleISBN(db, data);
			}),
		);
	}

	const parsedArtifacts = artifacts.filter(Boolean).map((artifact) => ({
		collection: "artifacts",
		data: {
			...Artifact.parse({
				...artifact,
				article: skippedArticles.includes(artifact?.article?.isbn) ? null : artifact?.article,
				work:
					(production.status === productionStatuses.DONE || api.isFinalizing) && work
						? WorkRef.parse(work)
						: null,
				producer: artifact?.article?.type === articleTypes.EBOOK ? null : OrganizationRef.parse(producer),
				publisher: OrganizationRef.parse(publisher),
				production: ProductionRef.parse(production),
				files: artifact.files.map(({ id }) => id),
				createdBy: { id: "migrationScript", firstName: "migrationScript", lastName: "", email: "" },
			}),
			imported: true,
		},
	}));

	const parsedArtifactFiles = artifacts.filter(Boolean).flatMap((artifact) =>
		artifact.files.map((file) => {
			const { id, ref } = createDocumentIdAndRef(
				{ api, env },
				{ collection: `artifacts/${artifact.id}/files`, id: file.id },
			);

			return {
				collection: `artifacts/${artifact.id}/files`,
				data: ArtifactFile.parse({ id, ref, ...file }),
				createdBy: { id: "migrationScript", firstName: "migrationScript", lastName: "", email: "" },
			};
		}),
	);

	const parsedWork = {
		collection: "works",
		data: Work.parse({
			...work,
			publisher: OrganizationRef.parse(publisher),
			//articleIds: articles.map((a) => a.id),
			serie: seriesRef ? seriesRef : null,
			season: season ? season : null,
			imprint: imprintRef ? imprintRef : null,
			created: api.admin.firestore.FieldValue.serverTimestamp(),
			createdBy: { id: "migrationScript", firstName: "migrationScript", lastName: "", email: "" },
		}),
	};

	const parsedSeries = newSeries && {
		collection: "series",
		data: Serie.parse({
			...newSeries,
			publisher: OrganizationRef.parse(publisher),
			articleIds: articles.map((a) => a.id),
			created: api.admin.firestore.Timestamp.fromDate(new Date()),
			createdBy: { id: "migrationScript", firstName: "migrationScript", lastName: "", email: "" },
		}),
	};
	const parsedImprint = newImprint && {
		collection: "imprints",
		data: Imprint.parse({
			...newImprint,
			publisher: OrganizationRef.parse(publisher),
			created: api.admin.firestore.Timestamp.fromDate(new Date()),
			createdBy: { id: "migrationScript", firstName: "migrationScript", lastName: "", email: "" },
		}),
	};

	const documents = [
		parsedWork,
		...parsedArticles.filter(({ data }) => !skippedArticles.includes(data?.isbn)),
		...parsedArtifacts,
		...parsedArtifactFiles,
		parsedImprint,
		parsedSeries,
	].filter(Boolean);

	// console.log(parsedArticles);

	if (doMigration) {
		await db.runTransaction(async (transaction) => {
			documents.forEach((doc) => {
				const docRef = db.collection(doc.collection).doc(doc.data.id);
				transaction.set(docRef, doc.data);
			});

			if (!api.convertFromLiveToStage) {
				const ref = db.collection("productions").doc(production.id);
				let update = {
					work: work ? WorkRef.parse(work) : null,
					imprint: imprintRef ? ImprintRef.parse(imprintRef) : null,
					articleIds: parsedArticles
						?.filter(({ data }) => !skippedArticles.includes(data?.isbn))
						.map((doc) => doc.data.id),
					articles: articlesMap,
					"migrations.flattening": {
						migrated: true,
						timestamp: new Date(),
						createdDocuments: documents.map((doc) => ({ collection: doc.collection, id: doc.data.id })),
					},
				};
				if (switchPublisher) {
					update.publisher = switchPublisher;
					if (switchPublisherName) {
						update.publisherName = switchPublisherName;
					}
				}
				transaction.update(ref, update);
			}
		});
	}

	return documents;
}
